import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import PortalRoute from "../components/PortalRoute";
import PublicRoute from "@/components/PublicRoute";
import Error404 from "../pages/error/Error404";
import Error401 from "../pages/error/Error401";
import MyErrorBoundary from "@/components/error/MyErrorBoundary.tsx";

const HomeDashboard = lazy(() => import("../pages/home/HomeDashboard"));
const Calendar = lazy(() => import("../pages/calendar/Calendar"));
const Settings = lazy(() => import("../pages/settings/settings"));
const SettingsDashboard = lazy(() => import("../pages/settings/overview/SettingsDashboard"));
const Users = lazy(() => import("../pages/settings/users/list/UserList"));
const PaymentGateway = lazy(() => import("../pages/settings/payment-gateway/PaymentGateways"));
const PaymentReceivers = lazy(() => import("../pages/settings/payment-receivers/PaymentReceiversList"));
const PaymentReceiverFormPage = lazy(() => import("../pages/settings/payment-receivers/PaymentReceiverFormPage"));
const TextTemplatingList = lazy(() => import("../pages/settings/templates/TextTemplateList"));
const Billing = lazy(() => import("../pages/settings/payment/PaymentForm"));
const OverviewPage = lazy(() => import("../pages/admin/OverviewPage"));
const AdminMenu = lazy(() => import("../pages/admin/AdminMenu"));
const OrganisationList = lazy(() => import("../pages/admin/organisation/OrganisationList"));
const BodyTypeList = lazy(() => import("../pages/admin/bodytypes/BodyTypeList"));
const BodyTypeForm = lazy(() => import("../pages/admin/bodytypes/BodyTypeForm"));
const FacilityList = lazy(() => import("../pages/admin/facility/FacilityList"));
const AdminUserList = lazy(() => import("../pages/admin/users/AdminUserList"));
const UserPricesPage = lazy(() => import("../pages/admin/users/UserPricesPage"));
const EIPage = lazy(() => import("../pages/admin/ei/EIPage"));
const PlayersList = lazy(() => import("../pages/players/PlayersList/PlayersList"));
const SelectPlayerForScreeningForm = lazy(() => import("../pages/screening/SelectPlayerForScreeningForm"));
const PlayerForm = lazy(() => import("../pages/players/PlayerForm/PlayerForm"));
const NewPlayerFlow = lazy(() => import("../pages/players/PlayerForm/NewPlayerFlow"));
const PlayerCard = lazy(() => import("../pages/players/PlayerCard"));
const EconomyPage = lazy(() => import("../pages/economy/EconomyPage"));
const EducationPage = lazy(() => import("../pages/education/EducationPage"));
const NotificationsPage = lazy(() => import("../pages/Notifications/NotificationsPage"));
const ProfilePage = lazy(() => import("../pages/profile/ProfilePage"));
const ICalInstructions = lazy(() => import("../pages/profile/ICalInstructions"));
const ScreeningForm = lazy(() => import("../pages/screening/ScreeningForm"));
const ScheduleManagement = lazy(() => import("@/pages/calendar/schedule/ScheduleManagement"));
const InviteList = lazy(() => import("@/pages/admin/invites/InviteList"));
const LoginPage = lazy(() => import("@/login/LoginPage"));
const ForgotPasswordPage = lazy(() => import("@/login/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/login/ResetPasswordPage"));
const PublicSignupPage = lazy(() => import("@/login/PublicSignupPage"));
const MultiStepSignupPage = lazy(() => import("@/login/MultiStepSignupPage"));
const CertificationSignupPage = lazy(() => import("@/login/CertificationSignupPage"));
const PlayerSelfAssessmentForm = lazy(() => import("@/pages/players/FormComponents/PlayerSelfAssessmentForm"));
const ServiceForm = lazy(() => import("@/pages/settings/overview/ServiceForm"));
const CourseForm = lazy(() => import("@/pages/settings/courses/CoursesForm"));
const CoursesList = lazy(() => import("@/pages/settings/courses/list/CoursesList"));
const EquipmentForm = lazy(() => import("@/pages/settings/overview/EquipmentForm"));
const EquipmentScheduleList = lazy(() => import("@/pages/settings/overview/EquipmentScheduleList"));
const EquipmentScheduleForm = lazy(() => import("@/pages/settings/overview/EquipmentScheduleForm"));
const EquipmentPricingList = lazy(() => import("@/pages/settings/overview/EquipmentPricingList"));
const EquipmentPricingForm = lazy(() => import("@/pages/settings/overview/EquipmentPricingForm"));
const PlayerSummary = lazy(() => import("@/pages/players/PlayerSummery"));
const PlayerCardTraining = lazy(() => import("@/pages/players/Pages/PlayerCardTraining"));
const PlayerGoalForm = lazy(() => import('@/pages/players/FormComponents/PlayerGoalForm'));
const MedicalDataForm = lazy(() => import('@/pages/players/FormComponents/MedicalDataForm'));
const PlayerTrainingForm = lazy(() => import('@/pages/players/FormComponents/PlayerTrainingForm'));
const MeasurementsPage = lazy(() => import('@/pages/players/Pages/MeasurementsPage'));
const ActionPlanPage = lazy(() => import('@/pages/players/Pages/ActionPlanPage'));
const ScreeningPage = lazy(() => import('@/pages/players/Pages/ScreeningPage'));
const CourseManagerPage = lazy(() => import("@/pages/settings/courses/CourseManagerPage"));
const CoursePaymentManager = lazy(() => import("@/pages/settings/courses/manager/CoursePaymentManager"));
const PrivacyPolicy = lazy(() => import("@/pages/public/PrivacyPolicy.tsx"));
const PaymentGatewaySwishForm = lazy(() => import("@/pages/settings/payment-gateway/PaymentGatewaySwishForm.tsx"));
const PaymentGatewayAtClubForm = lazy(() => import("@/pages/settings/payment-gateway/PaymentGatewayAtClubForm.tsx"));
const PaymentGatewayStripeForm = lazy(() => import("@/pages/settings/payment-gateway/PaymentGatewayStripeForm.tsx"));
const PaymentGatewayPayPalForm = lazy(() => import("@/pages/settings/payment-gateway/PaymentGatewayPayPalForm.tsx"));
const PaymentGatewayRevolutForm = lazy(() => import("@/pages/settings/payment-gateway/PaymentGatewayRevolutForm.tsx"));
const BookingSystemForm = lazy(() => import("@/pages/settings/booking-system/BookingSystemForm.tsx"));
const RegistrationPage = lazy(() => import("@/login/RegistrationPage.tsx"));
const OAuthConsentPage = lazy(() => import("@/pages/oauth/OAuthConsentPage"));
const ScreeningResultPage = lazy(() => import("@/pages/screening/ScreeningResultPage.tsx"));
const RecommendationPage = lazy(() => import("@/pages/players/Pages/RecommendationPage.tsx"));
const ScreeningAiWizardWrapper = lazy(() => import("@/pages/screening-ai-2/ScreeningAiWizardWrapper"));
const OrganisationForm = lazy(() => import("../pages/admin/organisation/OrganisationForm"));
const OrganisationUsersPage = lazy(() => import("../pages/admin/organisation/OrganisationUsersPage"));
const AdminOrganisationSubscriptionPage = lazy(() => import("../pages/admin/organisation/AdminOrganisationSubscriptionPage"));
const OrganisationEditForm = lazy(() => import("../pages/settings/organisation/OrganisationEditForm"));
const PlayerCardAppointments = lazy(() => import("@/pages/players/PlayerCardAppointments"));
const PackageForm = lazy(() => import("@/pages/settings/overview/PackageForm.tsx"));
const PromoCodeForm = lazy(() => import("@/pages/settings/overview/PromoCodeForm.tsx"));
const PlayerPackages = lazy(() => import('@/pages/players/Pages/PlayerPackages'));
const PlayerEconomyPage = lazy(() => import('@/pages/players/Pages/PlayerEconomyPage'));
const PlayerMessages = lazy(() => import("@/pages/players/PlayerMessages"));
const PlayerActivityPage = lazy(() => import("@/pages/players/Pages/PlayerActivityPage"));
const ChartWithDateFilter = lazy(() => import("@/pages/admin/statistic/ChartWithDateFilter.tsx"));
const ScreeningAIStatsPage = lazy(() => import("@/pages/admin/screeningStats/ScreeningAIStatsPage.tsx"));
const FacilityForm = lazy(() => import("@/pages/admin/facility/FacilityForm"));
const IntegrationsPage = lazy(() => import("@/pages/admin/integrations/IntegrationsPage"));
const SupportHub = lazy(() => import("@/pages/support/SupportHub.tsx"));
const BookingGettingStarted = lazy(() => import("@/pages/support/guides/BookingGettingStarted.tsx"));
const InstallIOSGuide = lazy(() => import("@/pages/support/guides/InstallIOSGuide.tsx"));
const FortnoxCallback = lazy(() => import("@/pages/admin/fortnox/FortnoxCallback"));
const PriceListsPage = lazy(() => import("@/pages/admin/pricelists/PriceListsPage"));
const PromotionCodesPage = lazy(() => import("@/pages/admin/promotioncodes/PromotionCodesPage"));
const SubscriptionsPage = lazy(() => import("@/pages/admin/SubscriptionsPage"));
const ClaudeConnectorPage = lazy(() => import("@/pages/admin/claude/ClaudeConnectorPage"));
const BGCoursesPage = lazy(() => import("@/pages/admin/bgCourses/BGCoursesPage"));
const OrganisationSubscriptionPage = lazy(() => import("@/pages/subscription/OrganisationSubscriptionPage"));
const CampaignsList = lazy(() => import("@/pages/campaigns/CampaignsList/CampaignsList"));
const CampaignNew = lazy(() => import("@/pages/campaigns/CampaignNew/CampaignNew"));
const CampaignStats = lazy(() => import("@/pages/campaigns/CampaignStats/CampaignStats"));
const EmailTemplatesList = lazy(() => import("@/pages/campaigns/Templates/EmailTemplatesList.tsx"));
const EmailTemplateForm = lazy(() => import("@/pages/campaigns/Templates/EmailTemplateForm.tsx"));
const TasksWizard = lazy(() => import("@/pages/tasks/TasksWizard.tsx"));
const ChatPage = lazy(() => import("@/pages/chat/ChatPage.tsx"));
const StatisticsPage = lazy(() => import("@/pages/statistics/StatisticsPage"));


export enum Routes {
    PORTAL = '/',
    PORTAL_PRIVACY_POLICY = '/privacy-policy',
    PORTAL_PUBLIC = '/login',
    PORTAL_PUBLIC_SIGNUP = '/login/signup',
    PORTAL_PUBLIC_SIGNUP_CERTIFICATION = '/login/signup/certification/:uuid',
    PORTAL_PUBLIC_SIGNUP_OLD = '/login/signup-old',
    PORTAL_PUBLIC_FORGOT_PASSWORD = '/login/forgot-password',
    PORTAL_PUBLIC_RESET_PASSWORD = '/login/reset-password/:token',
    PORTAL_PUBLIC_INVITE = '/login/invite/:token',
    PORTAL_CALENDAR = '/calendar',
    PORTAL_PLAYER = '/players',
    PORTAL_PLAYER_FORM = '/player/edit/:id',
    PORTAL_PLAYER_NEW = '/player/new',
    PORTAL_PLAYER_CARD = '/player/',
    PORTAL_PLAYER_SUMMERY = '/player/summery/:id',
    PORTAL_PLAYER_SCREENING = '/player/screening/:id',
    PORTAL_PLAYER_MEASUREMENTS = '/player/measurements/:id',
    PORTAL_PLAYER_ACTION_PLAN = '/player/action-plan/:id',
    PORTAL_PLAYER_TRAINING = '/player/training-plan/:id',
    PORTAL_PLAYER_SELF_ASSESSMENT_FORM = '/player/assessment-form/:player_id',
    PORTAL_PLAYER_GOAL_FORM = '/player/goal-form/:player_id/:goal_id?',
    PORTAL_PLAYER_MEDICAL_FORM = '/player/medical-form/:player_id/:medical_id?',
    PORTAL_PLAYER_TRAINING_FORM = '/player/training-form/:player_id/:training_id?',
    PORTAL_PLAYER_SCREENING_RESULT = '/player/screening-result/:id',
    PORTAL_PLAYER_SCREENING_RESULT_ACTION_PLAN = '/player/screening-result/:id/action-plan',
    PORTAL_PLAYER_SCREENING_RESULT_MEASUREMENTS = '/player/screening-result/:id/measurements',
    PORTAL_PLAYER_SCREENING_RESULT_RECOMMENDATIONS = '/player/screening-result/:id/recommendations',
    PORTAL_PLAYER_ACTIVITY = "/player/activity/:id",
    PORTAL_PLAYER_APPOINTMENTS = "/player/:id/appointments",
    PORTAL_PLAYER_PACKAGES = "/player/:id/packages",
    PORTAL_PLAYER_MESSAGES = "/player/:id/messages",
    PORTAL_PLAYER_ECONOMY = "/player/:id/economy",
    PORTAL_SCREENING = '/screening',
    PORTAL_SCREENING_FORM = '/screening-form/:id',
    PORTAL_ECONOMY = '/economy',
    PORTAL_ECONOMY_UNPAID = '/economy/unpaid',
    PORTAL_ECONOMY_PAID = '/economy/paid',
    PORTAL_ECONOMY_FAILED = '/economy/failed',
    PORTAL_ECONOMY_STATISTICS = '/economy/statistics',
    PORTAL_EDUCATION = '/education',
    PORTAL_STATISTICS = '/statistics',
    PORTAL_CAMPAIGNS = '/campaigns',
    PORTAL_CAMPAIGN_NEW = '/campaigns/new',
    PORTAL_CAMPAIGN_DETAILS = '/campaigns/:id',
    PORTAL_COURSES = '/courses',
    PORTAL_COURSES_FORM = '/course/form/:id?',
    PORTAL_COURSES_MANAGER = '/course/:id?',
    PORTAL_COURSES_PAYMENT = '/course/:id/payment',
    PORTAL_SETTINGS = '/settings',
    PORTAL_SETTINGS_DASHBOARD = '/settings/overview',
    PORTAL_SETTINGS_SERVICE_FORM = '/settings/service/form',
    PORTAL_SETTINGS_SERVICE_FORM_EDIT = '/settings/service/form/:id',
    PORTAL_SETTINGS_PACKAGE_FORM ='/settings/package/form/:id?',
    PORTAL_SETTINGS_PROMO_CODE_FORM ='/settings/promo-code/form/:id?',
    PORTAL_SETTINGS_EQUIPMENT_FORM = '/settings/equipment/form',
    PORTAL_SETTINGS_EQUIPMENT_FORM_EDIT = '/settings/equipment/form/:id',
    PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_LIST = '/settings/equipment/:equipmentId/schedules',
    PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_FORM = '/settings/equipment/:equipmentId/schedules/form',
    PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_FORM_EDIT = '/settings/equipment/:equipmentId/schedules/form/:scheduleId',
    PORTAL_SETTINGS_EQUIPMENT_PRICING_LIST = '/settings/equipment/:equipmentId/pricing',
    PORTAL_SETTINGS_EQUIPMENT_PRICING_FORM = '/settings/equipment/:equipmentId/pricing/form',
    PORTAL_SETTINGS_EQUIPMENT_PRICING_FORM_EDIT = '/settings/equipment/:equipmentId/pricing/form/:id',
    PORTAL_SETTINGS_SCHEDULE = '/settings/schedule',
    PORTAL_SETTINGS_USERS = '/settings/users',
    PORTAL_SETTINGS_PAYMENT_RECEIVERS = '/settings/payment-receivers',
    PORTAL_SETTINGS_PAYMENT_RECEIVERS_FORM = '/settings/payment-receivers/form',
    PORTAL_SETTINGS_PAYMENT_RECEIVERS_FORM_EDIT = '/settings/payment-receivers/form/:id',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY = '/settings/:paymentReceiverId/payment-gateway',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_SWISH = '/settings/:paymentReceiverId/payment-gateway/config/swish',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_AT_CLUB = '/settings/:paymentReceiverId/payment-gateway/config/at_club',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_STRIPE = '/settings/:paymentReceiverId/payment-gateway/config/stripe',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_PAYPAL = '/settings/:paymentReceiverId/payment-gateway/config/paypal',
    PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_REVOLUT = '/settings/:paymentReceiverId/payment-gateway/config/revolut',
    PORTAL_SETTINGS_PAYMENT_GATEWAY = '/settings/payment-gateway',
    PORTAL_SETTINGS_PAYMENT_GATEWAY_SWISH = '/settings/payment-gateway/config/swish',
    PORTAL_SETTINGS_PAYMENT_GATEWAY_AT_CLUB = '/settings/payment-gateway/config/at_club',
    PORTAL_SETTINGS_PAYMENT_GATEWAY_STRIPE = '/settings/payment-gateway/config/stripe',
    PORTAL_SETTINGS_PAYMENT_GATEWAY_PAYPAL = '/settings/payment-gateway/config/paypal',
    PORTAL_SETTINGS_PAYMENT_GATEWAY_REVOLUT = '/settings/payment-gateway/config/revolut',
    PORTAL_SETTINGS_TEXT_TEMPLATES = '/settings/text-template',
    PORTAL_SETTINGS_BOOKING_SYSTEM = '/settings/booking-system',
    PORTAL_SETTINGS_BILLING = '/settings/billing',
    PORTAL_SETTINGS_ORGANISATION_EDIT = '/settings/organisation/edit',
    PORTAL_SCREENING_AI = '/screening-ai',
    PORTAL_SCREENING_AI_ID = '/screening-ai/:id',
    PORTAL_SCREENING_AI_ID_MEASUREMENTS = '/screening-ai/:id/measurements',
    PORTAL_SCREENING_AI_ID_RECOMMENDATIONS = '/screening-ai/:id/recommendations',
    PORTAL_SCREENING_AI_ID_ACTION_PLAN = '/screening-ai/:id/action-plan',
    PORTAL_TASKS = '/tasks',
    PORTAL_ADMIN = '/admin',
    PORTAL_ADMIN_OVERVIEW = '/admin/overview',
    PORTAL_ADMIN_ORGANISATION = '/admin/organisation',
    PORTAL_ADMIN_ORGANISATION_FORM = '/admin/organisation/form/:id?',
    PORTAL_ADMIN_ORGANISATION_USERS = '/admin/organisation/:id/users',
    PORTAL_ADMIN_ORGANISATION_SUBSCRIPTION = '/admin/organisation/:id/subscription',
    PORTAL_ADMIN_FACILITYS = '/admin/facilitys',
    PORTAL_ADMIN_FACILITY_FORM = '/admin/facility/form/:id?',
    PORTAL_ADMIN_BODYTYPES = '/admin/bodytypes',
    PORTAL_ADMIN_BODYTYPE_FORM = '/admin/bodytypes/form/:id?',
    PORTAL_ADMIN_INVITES = '/admin/invites',
    PORTAL_ADMIN_USERS = '/admin/users',
    PORTAL_ADMIN_USER_PRICES = '/admin/user-prices',
    PORTAL_ADMIN_STATISTIC = '/admin/statistic',
    PORTAL_ADMIN_SCREENING_STATS = '/admin/screening-statistics',
    PORTAL_ADMIN_INTEGRATIONS = '/admin/integrations',
    PORTAL_ADMIN_FORTNOX_CALLBACK = '/admin/fortnox/callback',
    PORTAL_ADMIN_PRICE_LISTS = '/admin/price-lists',
    PORTAL_ADMIN_PROMOTION_CODES = '/admin/promotion-codes',
    PORTAL_ADMIN_BG_COURSES = '/admin/bg-courses',
    PORTAL_ADMIN_SUBSCRIPTIONS = '/admin/subscriptions',
    PORTAL_ADMIN_EI = '/admin/ei',
    PORTAL_ADMIN_CLAUDE = '/admin/claude',
    PORTAL_NOTIFICATIONS = '/notifications',
    PORTAL_CHAT = '/chat',
    PORTAL_PROFILE = '/profile',
    PORTAL_PROFILE_ICAL = '/profile/ical',
    PORTAL_SUBSCRIPTION = '/subscription',
    ACCESS_DENIED = '/access-denied',
    OAUTH_CONSENT = '/oauth/consent',
    PORTAL_EMAIL_TEMPLATES = '/email-templates',
    PORTAL_EMAIL_TEMPLATE_CREATE = '/email-templates/create',
    PORTAL_EMAIL_TEMPLATE_EDIT = '/email-templates/:id',
    PORTAL_SUPPORT = '/support',
    PORTAL_SUPPORT_GUIDE_BOOKING = '/support/guides/booking-get-started',
    PORTAL_SUPPORT_GUIDE_INSTALL_IOS = '/support/guides/install-ios',
}

export const router = createBrowserRouter([
    {
        path: "/",
        errorElement: <MyErrorBoundary/>,
        children: [
            {
                index: true,
                path: Routes.PORTAL,
                element: (
                    <PortalRoute>
                        <HomeDashboard/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING,
                element: (
                    <PortalRoute>
                        <SelectPlayerForScreeningForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_AI,
                element: (
                    <PortalRoute>
                        <ScreeningAiWizardWrapper/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_AI_ID,
                element: (
                    <PortalRoute>
                        <ScreeningAiWizardWrapper/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_AI_ID_MEASUREMENTS,
                element: (
                    <PortalRoute>
                        <ScreeningAiWizardWrapper/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_AI_ID_RECOMMENDATIONS,
                element: (
                    <PortalRoute>
                        <ScreeningAiWizardWrapper/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_AI_ID_ACTION_PLAN,
                element: (
                    <PortalRoute>
                        <ScreeningAiWizardWrapper/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_TASKS,
                element: (
                    <PortalRoute>
                        <TasksWizard/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SCREENING_FORM,
                element: (
                    <PortalRoute>
                        <ScreeningForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_SCREENING_RESULT,
                element: (
                    <PortalRoute>
                        <ScreeningResultPage/>
                    </PortalRoute>
                ),
                children: [
                    {path: Routes.PORTAL_PLAYER_SCREENING_RESULT_MEASUREMENTS, element: <MeasurementsPage/>},
                    {path: Routes.PORTAL_PLAYER_SCREENING_RESULT_ACTION_PLAN, element: <ActionPlanPage/>},
                    {path: Routes.PORTAL_PLAYER_SCREENING_RESULT_RECOMMENDATIONS, element: <RecommendationPage/>},

                ],
            },
            {
                path: Routes.PORTAL_PLAYER,
                element: (
                    <PortalRoute>
                        <PlayersList/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_CARD,
                element: (
                    <PortalRoute>
                        <PlayerCard/>
                    </PortalRoute>
                ),
                children: [
                    {path: Routes.PORTAL_PLAYER_SUMMERY, element: <PlayerSummary/>}, // Default to summary
                    {path: Routes.PORTAL_PLAYER_ACTIVITY, element: <PlayerActivityPage/>},
                    {path: Routes.PORTAL_PLAYER_SCREENING, element: <ScreeningPage/>},
                    {path: Routes.PORTAL_PLAYER_MEASUREMENTS, element: <MeasurementsPage/>},
                    {path: Routes.PORTAL_PLAYER_ACTION_PLAN, element: <ActionPlanPage/>},
                    {path: Routes.PORTAL_PLAYER_TRAINING, element: <PlayerCardTraining/>},
                    {path: Routes.PORTAL_PLAYER_APPOINTMENTS, element: <PlayerCardAppointments/>},
                    {path: Routes.PORTAL_PLAYER_PACKAGES, element: <PlayerPackages/>},
                    {path: Routes.PORTAL_PLAYER_MESSAGES, element: <PlayerMessages/>},
                    {path: Routes.PORTAL_PLAYER_ECONOMY, element: <PlayerEconomyPage/>},
                ],
            },
            {
                path: Routes.PORTAL_PLAYER_GOAL_FORM,
                element: (
                    <PortalRoute>
                        <PlayerGoalForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_MEDICAL_FORM,
                element: (
                    <PortalRoute>
                        <MedicalDataForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_SELF_ASSESSMENT_FORM,
                element: (
                    <PortalRoute>
                        <PlayerSelfAssessmentForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_TRAINING_FORM,
                element: (
                    <PortalRoute>
                        <PlayerTrainingForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_FORM,
                element: (
                    <PortalRoute>
                        <PlayerForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PLAYER_NEW,
                element: (
                    <PortalRoute>
                        <NewPlayerFlow/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_CALENDAR,
                element: (
                    <PortalRoute>
                        <Calendar/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_ECONOMY,
                element: <Navigate to={Routes.PORTAL_ECONOMY_UNPAID} replace />
            },
            {
                path: Routes.PORTAL_ECONOMY_UNPAID,
                element: (
                    <PortalRoute>
                        <EconomyPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_ECONOMY_PAID,
                element: (
                    <PortalRoute>
                        <EconomyPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_ECONOMY_FAILED,
                element: (
                    <PortalRoute>
                        <EconomyPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_ECONOMY_STATISTICS,
                element: (
                    <PortalRoute>
                        <EconomyPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_EDUCATION,
                element: (
                    <PortalRoute>
                        <EducationPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SETTINGS_SCHEDULE,
                element: (
                    <PortalRoute>
                        <ScheduleManagement/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_STATISTICS,
                element: (
                    <PortalRoute>
                        <StatisticsPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_CAMPAIGNS,
                element: (
                    <PortalRoute>
                        <CampaignsList/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_CAMPAIGN_NEW,
                element: (
                    <PortalRoute>
                        <CampaignNew/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_CAMPAIGN_DETAILS,
                element: (
                    <PortalRoute>
                        <CampaignStats/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_COURSES,
                element: (
                    <PortalRoute>
                        <CoursesList/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_COURSES_FORM,
                element: (
                    <PortalRoute>
                        <CourseForm/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_COURSES_PAYMENT,
                element: (
                    <PortalRoute>
                        <CoursePaymentManager/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_COURSES_MANAGER,
                element: (
                    <PortalRoute>
                        <CourseManagerPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_EMAIL_TEMPLATES,
                element: (
                    <PortalRoute>
                        <EmailTemplatesList/>
                    </PortalRoute>
                ),
            },
            {
                path: Routes.PORTAL_EMAIL_TEMPLATE_CREATE,
                element: (
                    <PortalRoute>
                        <EmailTemplateForm/>
                    </PortalRoute>
                ),
            },
            {
                path: Routes.PORTAL_EMAIL_TEMPLATE_EDIT,
                element: (
                    <PortalRoute>
                        <EmailTemplateForm/>
                    </PortalRoute>
                ),
            },
            {
                path: Routes.PORTAL_SETTINGS,
                element: (
                    <PortalRoute>
                        <Settings/>
                    </PortalRoute>
                ),
                children: [
                    {
                        path: Routes.PORTAL_SETTINGS_DASHBOARD, // Default path for `/settings`
                        element: <SettingsDashboard/>, // Optional default component
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_SERVICE_FORM, // Default path for `/settings`
                        element: <ServiceForm/>, // Optional default component
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_SERVICE_FORM_EDIT, // Edit service path
                        element: <ServiceForm/>, // Same component for editing
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PACKAGE_FORM, // Default path for `/settings`
                        element: <PackageForm/>, // Optional default component
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PROMO_CODE_FORM, // Default path for `/settings`
                        element: <PromoCodeForm/>, // Optional default component
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_FORM,
                        element: <EquipmentForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_FORM_EDIT,
                        element: <EquipmentForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_LIST,
                        element: <EquipmentScheduleList/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_FORM,
                        element: <EquipmentScheduleForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_SCHEDULE_FORM_EDIT,
                        element: <EquipmentScheduleForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_PRICING_LIST,
                        element: <EquipmentPricingList/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_PRICING_FORM,
                        element: <EquipmentPricingForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_EQUIPMENT_PRICING_FORM_EDIT,
                        element: <EquipmentPricingForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_USERS,
                        element: <Users/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVERS,
                        element: <PaymentReceivers/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVERS_FORM,
                        element: <PaymentReceiverFormPage/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVERS_FORM_EDIT,
                        element: <PaymentReceiverFormPage/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY,
                        element: <PaymentGateway/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY,
                        element: <PaymentGateway/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY_SWISH,
                        element: <PaymentGatewaySwishForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY_STRIPE,
                        element: <PaymentGatewayStripeForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY_PAYPAL,
                        element: <PaymentGatewayPayPalForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY_REVOLUT,
                        element: <PaymentGatewayRevolutForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_GATEWAY_AT_CLUB,
                        element: <PaymentGatewayAtClubForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_SWISH,
                        element: <PaymentGatewaySwishForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_STRIPE,
                        element: <PaymentGatewayStripeForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_PAYPAL,
                        element: <PaymentGatewayPayPalForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_REVOLUT,
                        element: <PaymentGatewayRevolutForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_PAYMENT_RECEIVER_GATEWAY_AT_CLUB,
                        element: <PaymentGatewayAtClubForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_TEXT_TEMPLATES,
                        element: <TextTemplatingList/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_BOOKING_SYSTEM,
                        element: <BookingSystemForm/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_BILLING,
                        element: <Billing/>,
                    },
                    {
                        path: Routes.PORTAL_SETTINGS_ORGANISATION_EDIT,
                        element: <OrganisationEditForm/>,
                    },
                ],
            },
            {
                path: Routes.PORTAL_ADMIN,
                element: (
                    <PortalRoute>
                        <AdminMenu/>
                    </PortalRoute>
                ),
                children: [
                    {
                        path: Routes.PORTAL_ADMIN_OVERVIEW,
                        element: <OverviewPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_ORGANISATION,
                        element: <OrganisationList/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_ORGANISATION_FORM,
                        element: <OrganisationForm/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_ORGANISATION_USERS,
                        element: <OrganisationUsersPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_ORGANISATION_SUBSCRIPTION,
                        element: <AdminOrganisationSubscriptionPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_FACILITYS,
                        element: <FacilityList/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_FACILITY_FORM,
                        element: <FacilityForm/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_BODYTYPES,
                        element: <BodyTypeList/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_BODYTYPE_FORM,
                        element: <BodyTypeForm/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_INVITES,
                        element: <InviteList/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_USERS,
                        element: <AdminUserList/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_USER_PRICES,
                        element: <UserPricesPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_STATISTIC,
                        element: <ChartWithDateFilter/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_SCREENING_STATS,
                        element: <ScreeningAIStatsPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_INTEGRATIONS,
                        element: <IntegrationsPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_FORTNOX_CALLBACK,
                        element: <FortnoxCallback/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_PRICE_LISTS,
                        element: <PriceListsPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_PROMOTION_CODES,
                        element: <PromotionCodesPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_BG_COURSES,
                        element: <BGCoursesPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_SUBSCRIPTIONS,
                        element: <SubscriptionsPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_EI,
                        element: <EIPage/>,
                    },
                    {
                        path: Routes.PORTAL_ADMIN_CLAUDE,
                        element: <ClaudeConnectorPage/>,
                    },

                ],
            },
            {
                path: Routes.PORTAL_NOTIFICATIONS,
                element: (
                    <PortalRoute>
                        <NotificationsPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_CHAT,
                element: (
                    <PortalRoute>
                        <ChatPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PROFILE,
                element: (
                    <PortalRoute>
                        <ProfilePage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PROFILE_ICAL,
                element: (
                    <PortalRoute>
                        <ICalInstructions/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SUBSCRIPTION,
                element: (
                    <PortalRoute>
                        <OrganisationSubscriptionPage/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PUBLIC,
                element: <PublicRoute/>, // Use PublicRoute for login-related routes
                children: [
                    {
                        path: Routes.PORTAL_PUBLIC,
                        element: <LoginPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_FORGOT_PASSWORD,
                        element: <ForgotPasswordPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_RESET_PASSWORD,
                        element: <ResetPasswordPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_INVITE,
                        element: <RegistrationPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_SIGNUP,
                        element: <MultiStepSignupPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_SIGNUP_CERTIFICATION,
                        element: <CertificationSignupPage/>
                    },
                    {
                        path: Routes.PORTAL_PUBLIC_SIGNUP_OLD,
                        element: <PublicSignupPage/>
                    },
                ]
            },
            {
                path: Routes.PORTAL_SUPPORT,
                element: (
                    <PortalRoute>
                        <SupportHub/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SUPPORT_GUIDE_BOOKING,
                element: (
                    <PortalRoute>
                        <BookingGettingStarted/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_SUPPORT_GUIDE_INSTALL_IOS,
                element: (
                    <PortalRoute>
                        <InstallIOSGuide/>
                    </PortalRoute>
                )
            },
            {
                path: Routes.PORTAL_PRIVACY_POLICY,
                element: <PrivacyPolicy/>,
            },
            {
                // Handles its own login redirect: Claude opens it from outside the portal.
                path: Routes.OAUTH_CONSENT,
                element: (
                    <Suspense fallback={null}>
                        <OAuthConsentPage/>
                    </Suspense>
                ),
            },
            {
                path: '*',
                element: <Error404/>
            },
            {
                path: Routes.ACCESS_DENIED,
                element: <Error401/>
            },
        ],
    },
]);
